import { CheckOutlined, InfoCircleFilled } from '@ant-design/icons'
import { Button, EmptyState, NoDataIcon, Table } from '@lmc/ui-kit'
import type { SorterResult } from 'antd/es/table/interface'
import { showToast } from 'common/utils'
import { Spinner } from 'components/generic'
import dayjs from 'dayjs'
import useMixpanelEvent, { MixpanelEvents } from 'hooks/useMixpanelEvent'
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import {
  useBulkUpdatePayBy,
  useGetReconciliation,
  useUpdateReconciliation
} from '../../Reports/hooks/useReconciliation'
import type { PayReportToReconciliationNavigationState } from '../../Reports/types/payReport'
import type { ReconciliationRow } from '../../Reports/types/reconciliation'
import { ReconciliationPayBy } from '../../Reports/types/reconciliation'
import { RotaDrawer } from '../RotaDrawer/RotaDrawer'
import {
  ReconciliationFiltersBar,
  useReconciliationFilters
} from './ReconciliationFilters'
import { CustomAmountModal, DontPayModal } from './ReconciliationModals'
import { getReconciliationTableColumns } from './reconciliationTableColumns'
import {
  formatDisplayName,
  isCompleteActualTime,
  mapReconciliationApiItemToRow,
  rowKey
} from './reconciliationUtils'

const ReconciliationCustomCellWrapper = ({
  children,
  ...props
}: {
  children: ReactNode
}) => <ReconciliationCustomCell {...props}>{children}</ReconciliationCustomCell>

const ReconciliationCustomTableWrapper = ({
  children,
  ...props
}: {
  children: ReactNode
}) => (
  <ReconciliationCustomTable {...props}>{children}</ReconciliationCustomTable>
)

const reconciliationTableComponents = {
  table: ReconciliationCustomTableWrapper,
  body: {
    cell: ReconciliationCustomCellWrapper
  }
}

export const PayRatesReconciliationTab = ({
  nominative
}: {
  nominative: string
}) => {
  const { t } = useTranslation()
  const sendEvent = useMixpanelEvent()

  const filterState = useReconciliationFilters(nominative)
  const {
    filters,
    page,
    setPage,
    setSortBy,
    setSortOrder,
    selectedRowKeys,
    setSelectedRowKeys,
    setDateRange,
    setTeamMemberFilter,
    setStatusFilter
  } = filterState

  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const st = location.state as PayReportToReconciliationNavigationState | null
    if (!st?.fromPayReport || !st.teamMemberIds?.length) {
      return
    }
    setTeamMemberFilter(st.teamMemberIds)
    setStatusFilter('needs_attention')
    if (st.startTime && st.endTime) {
      setDateRange([dayjs(st.startTime), dayjs(st.endTime)])
    }
    setPage(1)
    setSelectedRowKeys([])
    navigate(location.pathname, { replace: true, state: undefined })
  }, [
    location.pathname,
    location.state,
    navigate,
    setDateRange,
    setPage,
    setSelectedRowKeys,
    setStatusFilter,
    setTeamMemberFilter
  ])

  const bulkPayTypeOptions = useMemo(
    () => [
      {
        label: t('RECONCILIATION_PAY_BY_PLANNED'),
        value: ReconciliationPayBy.PLANNED_TIME
      },
      {
        label: t('RECONCILIATION_PAY_BY_ACTUAL'),
        value: ReconciliationPayBy.ACTUAL_TIME
      }
    ],
    [t]
  )

  type BulkPayBy =
    | ReconciliationPayBy.PLANNED_TIME
    | ReconciliationPayBy.ACTUAL_TIME
  const [bulkPayType, setBulkPayType] = useState<BulkPayBy>(
    ReconciliationPayBy.PLANNED_TIME
  )
  const [drawerInstanceId, setDrawerInstanceId] = useState<string | null>(null)
  const [dontPayRow, setDontPayRow] = useState<ReconciliationRow | null>(null)
  const [customAmountRow, setCustomAmountRow] =
    useState<ReconciliationRow | null>(null)

  const { data, isLoading, isFetching } = useGetReconciliation(filters)

  const rows = useMemo(() => {
    if (!data?.data?.length) {
      return []
    }
    return data.data
      .map(mapReconciliationApiItemToRow)
      .filter((row): row is ReconciliationRow => row != null)
  }, [data])
  const { mutate: bulkUpdatePayBy, isLoading: isBulkReconciling } =
    useBulkUpdatePayBy(() => setSelectedRowKeys([]))
  const { mutate: updateReconciliation, isLoading: isDontPayLoading } =
    useUpdateReconciliation(() => setDontPayRow(null))
  const { mutate: updateCustomAmount, isLoading: isCustomAmountLoading } =
    useUpdateReconciliation(() => setCustomAmountRow(null))

  const total = data?.pagination?.totalItems ?? 0

  const handleTableChange = (
    _pagination: unknown,
    _filters: unknown,
    sorter: SorterResult<ReconciliationRow> | SorterResult<ReconciliationRow>[],
    extra: { action: 'paginate' | 'sort' | 'filter' }
  ) => {
    const s = Array.isArray(sorter) ? sorter[0] : sorter
    if (s?.columnKey && s.order) {
      setSortBy(String(s.columnKey))
      setSortOrder(s.order === 'ascend' ? 'asc' : 'desc')
    } else {
      setSortBy(undefined)
      setSortOrder(undefined)
    }
    if (extra?.action !== 'paginate') {
      setPage(1)
      setSelectedRowKeys([])
    }
  }

  const handleBulkReconcile = () => {
    const selectedRows = rows.filter(
      (r: ReconciliationRow) =>
        selectedRowKeys.includes(rowKey(r)) &&
        !r.isCanceled &&
        Boolean(r.rotaInstanceId)
    )
    if (selectedRows.length === 0) {
      return
    }

    const incompleteActual =
      bulkPayType === ReconciliationPayBy.ACTUAL_TIME
        ? selectedRows.filter((r) => !isCompleteActualTime(r.actualTime))
        : []
    const hasError = incompleteActual.length > 0

    sendEvent(MixpanelEvents.RECONCILIATION_BULK_ACTION, {
      numberOfEventsSelected: selectedRowKeys.length,
      selectionType:
        bulkPayType === ReconciliationPayBy.PLANNED_TIME ? 'planned' : 'actual',
      filtersApplied: {
        status: 'status' in filters,
        discrepancy: 'durationDiscrepancy' in filters,
        teamMember: 'teamMemberIds' in filters,
        serviceUser: 'serviceUserIds' in filters,
        eventType: 'eventTypeIds' in filters
      },
      errorShown: hasError
    })

    if (hasError) {
      for (const r of incompleteActual) {
        const su = r.serviceUser?.name ?? '-'
        showToast({
          type: 'error',
          title: `${t('RECONCILIATION_TOAST_SKIPPED_ONE')}: ${formatDisplayName(r.teamMember.name)} → ${su} – ${r.eventType.name} (${r.date})`
        })
      }
      return
    }

    const paymentIds: [string, string][] = selectedRows.map(
      (r: ReconciliationRow) => [r.rotaInstanceId, r.teamMemberId]
    )

    bulkUpdatePayBy({ paymentIds, payBy: bulkPayType })
  }

  const handleDontPayClick = useCallback((record: ReconciliationRow) => {
    setDontPayRow(record)
  }, [])

  const handleEditPayAmount = useCallback((record: ReconciliationRow) => {
    setCustomAmountRow(record)
  }, [])

  const columns = useMemo(
    () =>
      getReconciliationTableColumns({
        t,
        nominative,
        onDontPay: handleDontPayClick,
        onEditPayAmount: handleEditPayAmount
      }),
    [t, nominative, handleDontPayClick, handleEditPayAmount]
  )

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => {
      setSelectedRowKeys(keys as string[])
      if (keys.length === 0) {
        setBulkPayType(ReconciliationPayBy.PLANNED_TIME)
      }
    },
    getCheckboxProps: (record: ReconciliationRow) => ({
      disabled: record.isCanceled
    }),
    columnTitle: (checkboxNode: ReactNode) => (
      <span data-testid="reconciliation-select-all">{checkboxNode}</span>
    ),
    renderCell: (
      _checked: boolean,
      _record: ReconciliationRow,
      _index: number,
      originNode: ReactNode
    ) => <span data-testid="reconciliation-row-checkbox">{originNode}</span>
  }

  const pagination = {
    current: page,
    pageSize: 10,
    total,
    onChange: (p: number) => {
      setPage(p)
      setSelectedRowKeys([])
    },
    showSizeChanger: false,
    position: ['bottomCenter'] as 'bottomCenter'[]
  }

  const hasRows = rows.length > 0
  const hasSelection = selectedRowKeys.length > 0
  const showTableLoader = (isLoading || isFetching) && !hasRows
  const showSelectHint = !hasSelection && !isLoading && hasRows
  const showEmpty = !isLoading && !isFetching && !hasRows

  const onRow = (record: ReconciliationRow) => ({
    'data-testid': 'reconciliation-table-row',
    onClick: (e: React.MouseEvent<HTMLElement>) => {
      const target = e.target as HTMLElement
      if (target.closest('.ant-table-selection-column')) {
        return
      }
      if (record.rotaInstanceId) {
        setDrawerInstanceId(record.rotaInstanceId)
      }
    },
    style: {
      cursor: record.rotaInstanceId ? 'pointer' : 'default'
    }
  })

  return (
    <>
      <TabBody>
        <ReconciliationFiltersBar {...filterState} />

        {showSelectHint ? (
          <BulkInfoBar>
            <BulkInfoIcon />
            <BulkInfoText>{t('RECONCILIATION_SELECT_ROWS_INFO')}</BulkInfoText>
          </BulkInfoBar>
        ) : hasSelection ? (
          <BulkActionBar>
            <BulkSelectionCount>
              {t('RECONCILIATION_N_SELECTED', {
                count: selectedRowKeys.length
              })}
            </BulkSelectionCount>
            <BulkToolbarDivider />
            <BulkRadioInner>
              {bulkPayTypeOptions.map((opt) => (
                <BulkRadioLabel key={opt.value}>
                  <BulkRadioInput
                    type="radio"
                    name="bulk-pay-type"
                    value={opt.value}
                    checked={bulkPayType === opt.value}
                    onChange={() => setBulkPayType(opt.value as BulkPayBy)}
                  />
                  {opt.label}
                </BulkRadioLabel>
              ))}
            </BulkRadioInner>
            <BulkReconcileWrap>
              <Button
                variant="primary"
                onClick={handleBulkReconcile}
                isLoading={isBulkReconciling}
                icon={<CheckOutlined />}
              >
                {t('RECONCILIATION_RECONCILE')}
              </Button>
            </BulkReconcileWrap>
          </BulkActionBar>
        ) : null}

        {showTableLoader ? (
          <TableLoaderWrap>
            <Spinner />
          </TableLoaderWrap>
        ) : showEmpty ? (
          <EmptyStateWrap>
            <EmptyState
              icon={<NoDataIcon />}
              title={t('RECONCILIATION_EMPTY_TITLE')}
              description={t('RECONCILIATION_EMPTY_DESCRIPTION')}
            />
          </EmptyStateWrap>
        ) : (
          <ReconciliationTableScroll>
            <StyledTable
              components={reconciliationTableComponents}
              columns={columns}
              dataSource={rows}
              rowKey={rowKey}
              loading={isFetching}
              rowClassName={(_, index) =>
                index % 2 === 0 ? 'even-row' : 'odd-row'
              }
              onRow={onRow}
              rowSelection={rowSelection}
              pagination={pagination}
              onChange={handleTableChange}
              size="small"
              scroll={{ x: 'max-content' }}
            />
          </ReconciliationTableScroll>
        )}
      </TabBody>

      {drawerInstanceId && (
        <RotaDrawer
          data-testid="reconciliation-event-drawer"
          isVisible={true}
          id={drawerInstanceId}
          onClose={() => setDrawerInstanceId(null)}
        />
      )}

      <DontPayModal
        open={dontPayRow !== null}
        row={dontPayRow}
        onClose={() => setDontPayRow(null)}
        isLoading={isDontPayLoading}
        onSave={(row, reason) => {
          sendEvent(MixpanelEvents.RECONCILIATION_DONT_PAY, {
            teamMember: row.teamMember.name,
            eventName: row.eventType.name,
            eventTypeId: row.eventType.id,
            startTime: row.plannedTime.startTime,
            isCanceled: row.isCanceled,
            isMissed: !row.isCanceled && row.actualTime === null,
            reason
          })
          updateReconciliation({
            rotaInstanceId: row.rotaInstanceId,
            payload: {
              teamMemberId: row.teamMemberId,
              reconciliationStatus: 'reconciled',
              payBy: ReconciliationPayBy.DONT_PAY,
              dontPayReason: reason
            }
          })
        }}
      />

      <CustomAmountModal
        open={customAmountRow !== null}
        row={customAmountRow}
        onClose={() => setCustomAmountRow(null)}
        isLoading={isCustomAmountLoading}
        onSave={(row, amount) => {
          sendEvent(MixpanelEvents.RECONCILIATION_CUSTOM_PAY_AMOUNT, {
            teamMember: row.teamMember.name,
            eventName: row.eventType.name,
            eventTypeId: row.eventType.id,
            startTime: row.plannedTime.startTime,
            originalPayAmount: row.payAmount,
            newPayAmount: amount
          })
          updateCustomAmount({
            rotaInstanceId: row.rotaInstanceId,
            payload: {
              teamMemberId: row.teamMemberId,
              reconciliationStatus: 'reconciled',
              payBy: ReconciliationPayBy.CUSTOM_AMOUNT,
              customAmountToPay: String(amount)
            }
          })
        }}
      />
    </>
  )
}

export default PayRatesReconciliationTab

const TabBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const EmptyStateWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 320px;
  width: 100%;
  border-radius: 20px;
  background: ${({ theme }) => theme.color.neutral.color10};
`

const TableLoaderWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 320px;
  width: 100%;
  border-radius: 20px;
  background: ${({ theme }) => theme.color.neutral.color10};
`

const ReconciliationTableScroll = styled.div`
  width: 100%;
  min-width: 0;
  overflow-x: auto;
  border-radius: 20px;
`

const StyledTable = styled(Table)`
  & .ant-table {
    min-width: max-content;
  }

  & .ant-table-thead > tr > th {
    white-space: nowrap;
  }

  & .ant-table-column-sorters {
    flex-direction: row-reverse;
    justify-content: flex-end;
  }

  & .ant-table-column-sorter {
    margin-right: 4px;
    margin-left: 0;
  }

  & .ant-table-thead > tr > th.reconciliation-header-multiline {
    white-space: pre-line;
    vertical-align: bottom;
  }

  & .even-row {
    background-color: ${({ theme }) => theme.color.neutral.color11};
  }

  & .odd-row {
    background-color: ${({ theme }) => theme.color.neutral.color10};
  }

  & .ant-table-tbody > tr.ant-table-row-selected > td {
    background: inherit;
  }
` as typeof Table

const BulkToolbarSurface = styled.div`
  display: flex;
  align-items: center;
  box-sizing: border-box;
  min-height: 56px;
  padding: 10px 16px;
  background: ${({ theme }) => theme.color.neutral.color11};
  border: 1px solid ${({ theme }) => theme.color.neutral.color9};
  border-radius: 8px;
`

const BulkInfoBar = styled(BulkToolbarSurface)`
  gap: 12px;
`

const BulkInfoIcon = styled(InfoCircleFilled)`
  color: ${({ theme }) => theme.color.status.primary};
  font-size: 14px;
  flex-shrink: 0;
`

const BulkInfoText = styled.span`
  color: ${({ theme }) => theme.color.neutral.color3};
  font-size: 14px;
  line-height: 1.4;
`

const BulkActionBar = styled(BulkToolbarSurface)`
  flex-wrap: wrap;
  gap: 16px;
`

const BulkSelectionCount = styled.span`
  color: ${({ theme }) => theme.color.neutral.color2};
  font-weight: 500;
  flex-shrink: 0;
`

const BulkToolbarDivider = styled.div`
  width: 1px;
  align-self: stretch;
  min-height: 24px;
  background: ${({ theme }) => theme.color.neutral.color8};
  flex-shrink: 0;
`

const BulkReconcileWrap = styled.div`
  margin-left: auto;
  display: flex;
`

const BulkRadioInner = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const BulkRadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
`

const BulkRadioInput = styled.input`
  accent-color: #1677ff;
  cursor: pointer;
  margin: 0;
`

const ReconciliationCustomTable = styled.table`
  background: ${({ theme }) => theme.color.background.tertiary};
`

const ReconciliationCustomCell = styled.td`
  padding: 16px 12px !important;
  background-color: inherit !important;
`
